<template>
  <div
    v-if="isVisible"
    class="tw:fixed tw:inset-0 tw:z-20 tw:flex tw:items-center tw:justify-center tw:bg-[rgba(22,30,45,0.2)] tw:p-5"
    @click.self="onBackdropClick"
  >
    <div
      class="tw:relative tw:w-full tw:max-w-[390px] tw:rounded-xl tw:bg-white tw:p-6 tw:shadow-[0_8px_24px_rgba(20,27,40,0.12)]"
    >
      <div class="tw:flex tw:items-center tw:justify-between">
        <h4
          class="tw:m-0 tw:text-3xl tw:font-medium tw:leading-8 tw:tracking-[-2px] tw:text-[#222629] tw:lg:text-3xl"
        >
          Add field
        </h4>
        <button
          type="button"
          class="tw:-mr-2 tw:-mt-2 tw:flex tw:h-12 tw:w-12 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[10px] tw:border-0 tw:bg-transparent tw:p-0 tw:text-[#7f8da1] tw:hover:bg-[#f2f4f7]"
          aria-label="close add field modal"
          :disabled="showSpinner"
          @click="closeModal"
        >
          <span class="tw:-mt-1 tw:block tw:text-5xl tw:leading-none">×</span>
        </button>
      </div>
      <p class="tw:mt-6 tw:text-sm tw:leading-5 tw:text-[#48586e]">
        If you add credentials and one-time codes, the scan can reach authenticated screens and the
        report may include more findings.
      </p>
      <div class="tw:mt-6 tw:flex tw:flex-col tw:gap-3.5">
        <div class="tw:relative tw:w-full">
          <select
            v-model="addFieldForm.fieldKey"
            :disabled="showSpinner"
            class="tw:h-12 tw:w-full tw:appearance-none tw:rounded-lg tw:border-0 tw:bg-[#f2f4f7] tw:px-4 tw:pr-11 tw:text-sm tw:text-[#5d6c7f]"
          >
            <option disabled value="">Type of field</option>
            <option v-for="option in fieldOptions" :key="option.fieldKey" :value="option.fieldKey">
              {{ option.label }}
            </option>
          </select>
          <span
            class="tw:pointer-events-none tw:absolute tw:right-[18px] tw:top-1/2 tw:block tw:h-2.5 tw:w-2.5 tw:-translate-y-1/2 tw:rotate-45 tw:border-b-2 tw:border-r-2 tw:border-[#222629]"
          />
        </div>
        <input
          v-if="isCustomFieldSelected"
          v-model.trim="addFieldForm.name"
          type="text"
          :disabled="showSpinner"
          class="tw:h-12 tw:w-full tw:rounded-lg tw:border-0 tw:bg-[#f2f4f7] tw:px-4 tw:text-sm tw:text-[#5d6c7f]"
          placeholder="Name"
        />
        <input
          v-model.trim="addFieldForm.value"
          type="text"
          :disabled="showSpinner"
          class="tw:h-12 tw:w-full tw:rounded-lg tw:border-0 tw:bg-[#f2f4f7] tw:px-4 tw:text-sm tw:text-[#5d6c7f]"
          placeholder="Value"
        />
        <p
          v-if="isEmailFieldSelected && addFieldForm.value && !isEmailValueValid"
          class="tw:mx-0.5 tw:-mt-1 tw:text-[13px] tw:leading-[18px] tw:text-[#d14f45]"
        >
          Value should be a valid email.
        </p>
        <button
          type="button"
          class="tw:mt-1.5 tw:h-12 tw:w-full tw:rounded-xl tw:border-0 tw:bg-[#0063fc] tw:text-sm tw:leading-5 tw:text-white tw:transition-opacity tw:hover:opacity-90 tw:disabled:cursor-default tw:disabled:opacity-55"
          :disabled="!canAddField || showSpinner || isDisabled"
          @click="addField"
        >
          Add
        </button>
      </div>
      <div
        v-if="showSpinner"
        class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:rounded-xl tw:bg-[rgba(255,255,255,0.72)]"
      >
        <Spinner />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import Spinner from '@/components/Spinner.vue';
import { isValidEmail } from '@/utils/validation';
import {
  CUSTOM_FIELD_OPTION,
  CUSTOM_FIELD_PREFIX,
  SCANNER_CREDENTIAL_FIELDS,
} from '@/_core/entities/scanner-credential-fields/models/constants';
import type {
  TAddScannerCredentialFieldForm,
  TNormalizedScannerCredentialField,
  TScannerCredentialFieldOption,
  TSubmitScannerCredentialField,
} from '@/_core/entities/scanner-credential-fields/models/types';

type TScannerCredentialFieldsAddFieldModalProps = {
  fields: TNormalizedScannerCredentialField[];
  isDisabled?: boolean;
  showSpinner?: boolean;
  closeModal: () => void;
  submitField: TSubmitScannerCredentialField;
};

const props = withDefaults(defineProps<TScannerCredentialFieldsAddFieldModalProps>(), {
  isDisabled: false,
  showSpinner: false,
});

const isVisible = ref(true);

const addFieldForm = reactive<TAddScannerCredentialFieldForm>({
  fieldKey: '',
  name: '',
  value: '',
});

const fieldOptions = computed<TScannerCredentialFieldOption[]>(() => {
  const activeStandardKeys = new Set(
    props.fields
      .map((field: TNormalizedScannerCredentialField) => field.fieldType)
      .filter((fieldType: string) =>
        SCANNER_CREDENTIAL_FIELDS.some(
          (field: TScannerCredentialFieldOption) => field.fieldKey === fieldType
        )
      )
  );

  const standardOptions = SCANNER_CREDENTIAL_FIELDS.filter(
    (field: TScannerCredentialFieldOption) => !activeStandardKeys.has(field.fieldKey)
  );

  return [...standardOptions, CUSTOM_FIELD_OPTION];
});

const isCustomFieldSelected = computed(
  () => addFieldForm.fieldKey === CUSTOM_FIELD_OPTION.fieldKey
);

const isEmailFieldSelected = computed(() => addFieldForm.fieldKey === 'email');

const isEmailValueValid = computed(() => {
  if (!isEmailFieldSelected.value) {
    return true;
  }

  return isValidEmail(addFieldForm.value);
});

const canAddField = computed(() => {
  if (!addFieldForm.fieldKey) {
    return false;
  }

  if (!addFieldForm.value) {
    return false;
  }

  if (!isEmailValueValid.value) {
    return false;
  }

  if (!isCustomFieldSelected.value) {
    return true;
  }

  return !!addFieldForm.name;
});

function resetForm(): void {
  addFieldForm.fieldKey = '';
  addFieldForm.name = '';
  addFieldForm.value = '';
}

function closeModal(): void {
  if (!isVisible.value) {
    return;
  }

  resetForm();
  isVisible.value = false;
  props.closeModal();
}

function onBackdropClick(): void {
  if (props.showSpinner) {
    return;
  }

  closeModal();
}

function buildField(): TNormalizedScannerCredentialField | null {
  const selectedFieldKey = addFieldForm.fieldKey;
  const value = addFieldForm.value;

  if (selectedFieldKey === CUSTOM_FIELD_OPTION.fieldKey) {
    if (!addFieldForm.name) {
      return null;
    }

    const name = addFieldForm.name;

    return {
      fieldKey: generateCustomFieldKey(name),
      fieldType: CUSTOM_FIELD_OPTION.fieldKey,
      label: name,
      name,
      value,
    };
  }

  const selectedDefinition = SCANNER_CREDENTIAL_FIELDS.find(
    (field: TScannerCredentialFieldOption) => field.fieldKey === selectedFieldKey
  );

  if (!selectedDefinition) {
    return null;
  }

  return {
    fieldKey: selectedDefinition.fieldKey,
    fieldType: selectedDefinition.fieldKey,
    label: selectedDefinition.label,
    name: '',
    value,
  };
}

function generateCustomFieldKey(name: string): string {
  const encodedName = encodeURIComponent(name).slice(0, 60);
  const randomSuffix = crypto.randomUUID();

  return `${CUSTOM_FIELD_PREFIX}${encodedName}__${randomSuffix}`;
}

async function addField(): Promise<void> {
  if (!canAddField.value) {
    return;
  }

  if (props.isDisabled || props.showSpinner) {
    return;
  }

  const nextField = buildField();

  if (!nextField) {
    return;
  }

  const isAdded = await props.submitField(nextField);

  if (!isAdded) {
    return;
  }

  closeModal();
}
</script>

<style lang="scss" scoped></style>
